import * as React from "react";
import type { CogoElement } from "../core/types";
import { resolveStyleTokens } from "../lib/style-token-resolver";

type UnknownRecord = Record<string, unknown>;

interface SourceFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

const SOURCE_STYLE_KEYS = new Set([
  "background",
  "backgroundColor",
  "border",
  "borderColor",
  "borderRadius",
  "borderStyle",
  "borderWidth",
  "boxShadow",
  "color",
  "display",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "height",
  "inset",
  "justifyContent",
  "left",
  "alignItems",
  "letterSpacing",
  "lineHeight",
  "margin",
  "maxHeight",
  "maxWidth",
  "minHeight",
  "minWidth",
  "opacity",
  "overflow",
  "padding",
  "pointerEvents",
  "position",
  "right",
  "textAlign",
  "top",
  "transform",
  "whiteSpace",
  "width",
  "zIndex",
]);

const SOURCE_FONT_FALLBACK = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"Apple SD Gothic Neo"',
  '"Noto Sans KR"',
  '"Segoe UI"',
  "sans-serif",
];

export function isSourceDerivedElement(element: CogoElement): boolean {
  const raw = element as unknown as UnknownRecord;
  const options = asRecord(raw.options);
  return (
    options?.sourceDerived === true ||
    raw.source !== undefined ||
    raw.sourceDesign !== undefined ||
    raw.sourceFactRefs !== undefined ||
    raw.sourceNodeRefs !== undefined ||
    raw.componentContract !== undefined ||
    raw.surfaceRef !== undefined ||
    hasSourceVectorPayload(element)
  );
}

export function readSourceFrame(element: CogoElement): SourceFrame | null {
  const frame = asRecord((element as unknown as UnknownRecord).frame);
  if (!frame) return null;
  const x = numberValue(frame.x);
  const y = numberValue(frame.y);
  const width = numberValue(frame.width);
  const height = numberValue(frame.height);
  if (x == null || y == null || width == null || height == null) return null;
  if (width <= 0 || height <= 0) return null;
  return { x, y, width, height };
}

export function shouldWrapSourceFrame(element: CogoElement): boolean {
  if (!isSourceDerivedElement(element)) return false;
  if (!readSourceFrame(element)) return false;
  return element.type !== "screen" && element.type !== "application";
}

export function isPassiveSourceHitTarget(element: CogoElement): boolean {
  if (!isSourceDerivedElement(element)) return false;
  const events = (element as unknown as { events?: { tap?: unknown } }).events;
  if (events?.tap) return false;
  return element.type === "text" || element.type === "shape";
}

export function sourceFrameWrapperStyle(
  element: CogoElement,
): React.CSSProperties | undefined {
  const frame = readSourceFrame(element);
  if (!frame) return undefined;
  return {
    position: "absolute",
    left: frame.x,
    top: frame.y,
    width: frame.width,
    height: frame.height,
  };
}

export function sourceInlineStyle(
  element: CogoElement,
  designTokens?: Parameters<typeof resolveStyleTokens>[1],
): React.CSSProperties | undefined {
  const style = asRecord((element as unknown as UnknownRecord).style);
  if (!style) return undefined;

  const cssStyle: React.CSSProperties = {};
  for (const [key, value] of Object.entries(style)) {
    if (!SOURCE_STYLE_KEYS.has(key)) continue;
    if (key === "fontFamily") {
      cssStyle.fontFamily = sourceFontFamily(value);
      continue;
    }
    if (key === "lineHeight") {
      cssStyle.lineHeight = sourceLineHeight(value, style.fontSize);
      continue;
    }
    if (key === "letterSpacing") {
      cssStyle.letterSpacing = sourceCssLength(value);
      continue;
    }
    (cssStyle as UnknownRecord)[key] = value;
  }

  if (cssStyle.fontFamily == null && style.fontFamily != null) {
    cssStyle.fontFamily = sourceFontFamily(style.fontFamily);
  }
  if (cssStyle.lineHeight == null) {
    cssStyle.lineHeight = sourceLineHeight(style.lineHeightPx, style.fontSize);
  }
  if (cssStyle.textAlign == null) {
    cssStyle.textAlign = sourceTextAlign(style.textAlignHorizontal);
  }
  if (cssStyle.letterSpacing == null) {
    cssStyle.letterSpacing = sourceCssLength(style.letterSpacing);
  }

  const resolved = resolveStyleTokens(cssStyle, designTokens);
  return Object.keys(resolved).length > 0 ? resolved : undefined;
}

export function sourceFillBoxStyle(
  element: CogoElement,
  designTokens?: Parameters<typeof resolveStyleTokens>[1],
): React.CSSProperties {
  return {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    ...sourceInlineStyle(element, designTokens),
  };
}

export function sourceTextBoxStyle(
  element: CogoElement,
  designTokens?: Parameters<typeof resolveStyleTokens>[1],
): React.CSSProperties {
  const sourceStyle = sourceInlineStyle(element, designTokens) ?? {};
  const hasFrame = readSourceFrame(element) != null;
  return {
    ...sourceStyle,
    ...(hasFrame
      ? {
          width: "100%",
          height: "100%",
        }
      : {}),
    minWidth: 0,
    minHeight: 0,
    display: "block",
    boxSizing: "border-box",
    whiteSpace: sourceStyle.whiteSpace ?? "pre-line",
    overflow: sourceStyle.overflow ?? "hidden",
  };
}

export function sourceFontFamily(
  value: unknown,
): React.CSSProperties["fontFamily"] {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return SOURCE_FONT_FALLBACK.join(", ");
  if (raw.includes(",") || raw.startsWith("var(")) return raw;
  const family =
    raw.includes(" ") && !raw.startsWith('"') && !raw.startsWith("'")
      ? `"${raw}"`
      : raw;
  return [family, ...SOURCE_FONT_FALLBACK].join(", ");
}

export function hasSourceVectorPayload(element: CogoElement): boolean {
  const payload = sourceVectorPayload(element);
  return Array.isArray(payload?.paths) && payload.paths.length > 0;
}

export function renderSourceVectorPayload(
  element: CogoElement,
  className = "cogo-source-vector-payload",
): React.ReactElement | null {
  const payload = sourceVectorPayload(element);
  const paths = Array.isArray(payload?.paths) ? payload.paths : [];
  if (paths.length === 0) return null;

  const width =
    numberValue(payload?.width) ?? readSourceFrame(element)?.width ?? 1;
  const height =
    numberValue(payload?.height) ?? readSourceFrame(element)?.height ?? 1;
  if (width <= 0 || height <= 0) return null;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${round(width)} ${round(height)}`}
      aria-hidden="true"
      focusable="false"
      data-cogo-source-vector-payload="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      {paths.map((item, index) => {
        const path = asRecord(item);
        if (!path) return null;
        const d = typeof path?.d === "string" ? path.d : "";
        if (!d) return null;
        return (
          <path
            key={`${String(path.sourceNodeId ?? index)}-${index}`}
            d={d}
            fill={typeof path.fill === "string" ? path.fill : "currentColor"}
            fillOpacity={numberValue(path.fillOpacity) ?? undefined}
            fillRule={path.fillRule === "evenodd" ? "evenodd" : "nonzero"}
            transform={svgTransform(path.transform)}
          />
        );
      })}
    </svg>
  );
}

function sourceVectorPayload(element: CogoElement): UnknownRecord | null {
  const style = asRecord((element as unknown as UnknownRecord).style);
  const styling = asRecord((element as unknown as UnknownRecord).styling);
  return asRecord(style?.vectorPayload) ?? asRecord(styling?.vectorPayload);
}

function svgTransform(transform: unknown): string | undefined {
  const matrix = asRecord(transform)?.matrix;
  if (!Array.isArray(matrix) || matrix.length !== 6) return undefined;
  const values = matrix.map((item) => numberValue(item));
  if (values.some((item) => item == null)) return undefined;
  return `matrix(${values.map((item) => round(item ?? 0)).join(" ")})`;
}

function sourceLineHeight(
  value: unknown,
  fontSizeValue: unknown,
): React.CSSProperties["lineHeight"] | undefined {
  const numeric = numberValue(value);
  if (numeric != null) return numeric >= 4 ? `${round(numeric)}px` : numeric;
  if (typeof value === "string" && value.trim()) return value;
  const fontSize = numberValue(fontSizeValue);
  return fontSize != null ? `${round(fontSize * 1.4)}px` : undefined;
}

function sourceCssLength(
  value: unknown,
): React.CSSProperties["letterSpacing"] | undefined {
  const numeric = numberValue(value);
  if (numeric != null) return `${round(numeric)}px`;
  return typeof value === "string" && value.trim() ? value : undefined;
}

function sourceTextAlign(
  value: unknown,
): React.CSSProperties["textAlign"] | undefined {
  const raw = typeof value === "string" ? value.toLowerCase() : "";
  if (raw === "center") return "center";
  if (raw === "right") return "right";
  if (raw === "justified" || raw === "justify") return "justify";
  if (raw === "left") return "left";
  return undefined;
}

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function numberValue(value: unknown): number | null {
  const number =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : NaN;
  return Number.isFinite(number) ? number : null;
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
